import { CtaForm, SelectOption, FormRecord } from '@pairy/sheard/types';
import { Button, Input, Stack, Text } from '@pairy/pds-react';
import { KeyboardEvent } from 'react';

import * as Styled from './index.styles';
import FormInput from './FormInput';
import PersonalInformation from './PersonalInformation';
import NumberInput from './NumberInput';
import CheckBox from './CheckBox';
import Select from './Select';
import Legal from './Legal';

interface Props {
  formFields: FormRecord[];
  fields?: CtaForm['fields'];
  handleChangeWithId: (id: number, value: FormRecord['value']) => void;
  translateKoToEn: (item: string) => string;

  // 이메일 인증 관련
  isEmailVerificationEnabled: boolean;
  isCodeSent?: boolean;
  isVerified?: boolean;
  verificationCode?: string;
  onSendVerificationCode?: (email: string) => void;
  onVerifyCode?: (code: string) => void;
  onChangeVerificationCode?: (value: string) => void;
}

const CtaFormModalContents = ({
  formFields,
  handleChangeWithId,
  translateKoToEn,
  fields,

  isEmailVerificationEnabled,
  isCodeSent = false,
  isVerified = false,
  verificationCode = '',
  onSendVerificationCode = () => {},
  onVerifyCode = () => {},
  onChangeVerificationCode = () => {},
}: Props) => {
  const consentFields =
    fields?.filter((f) => f.type === 'legal' || f.type === 'private') ?? [];
  const consentFieldIds = consentFields.map((f) => f.id);
  const firstConsentIndex =
    fields?.findIndex((f) => f.type === 'legal' || f.type === 'private') ?? -1;
  const showAgreeAll = consentFieldIds.length >= 2;

  const allChecked =
    showAgreeAll &&
    consentFieldIds.every((id) =>
      Boolean(formFields.find((f) => f.id === id)?.value),
    );

  const handleAgreeAll = () => {
    consentFieldIds.forEach((id) => handleChangeWithId(id, !allChecked));
  };

  const renderConsentGroup = () => (
    <Stack key="consent-group" direction="column" gap={16}>
      {showAgreeAll && (
        <>
          <Text variant="sm-semibold" as="label">
            <Stack gap={4} alignItems="center">
              <Styled.MinWidthWrapper width={24} height={24}>
                <Styled.CheckBoxInput
                  type="checkbox"
                  onChange={handleAgreeAll}
                  checked={allChecked}
                />
              </Styled.MinWidthWrapper>
              <Styled.KeepAllText variant="md-semibold">
                {translateKoToEn('agree-all')}
              </Styled.KeepAllText>
            </Stack>
          </Text>
          <hr
            style={{
              border: 'none',
              borderTop: '1px solid #e5e7eb',
              margin: 0,
            }}
          />
        </>
      )}
      {consentFields.map((field) => {
        if (field.type === 'legal') {
          return (
            <Legal
              id={field.id}
              key={field.id}
              title={field.name}
              description={field.description}
              isRequired={field.isRequired}
              link={field.link}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              value={
                formFields.find((f) => f.id === field.id)?.value as boolean
              }
            />
          );
        }
        if (field.type === 'private') {
          return (
            <PersonalInformation
              id={field.id}
              key={field.id}
              title={field.name}
              description={field.description}
              ctaField={fields}
              template={field?.template}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              value={
                formFields.find((f) => f.id === field.id)?.value as boolean
              }
            />
          );
        }
      })}
    </Stack>
  );

  return (
    <>
      {fields?.flatMap((field, index) => {
        const isConsent = field.type === 'legal' || field.type === 'private';

        if (isConsent && index !== firstConsentIndex) {
          return [];
        }
        if (isConsent && index === firstConsentIndex) {
          return [renderConsentGroup()];
        }

        if (field.type === 'checkBox') {
          const id = field.id;
          return [
            <CheckBox
              id={id}
              key={id}
              title={field.name}
              description={field.description}
              isRequired={field.isRequired}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              value={formFields.find((f) => f.id === id)?.value as boolean}
            />,
          ];
        }
        if (field.type === 'number') {
          const id = field.id;
          return [
            <NumberInput
              id={field.id}
              key={field.id}
              title={field.name}
              isRequired={field.isRequired}
              description={field.description}
              placeholder={field.placeholder}
              max={field.range?.max}
              min={field.range?.min}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              value={formFields.find((f) => f.id === id)?.value as number}
            />,
          ];
        }
        if (field.type === 'select') {
          const id = field.id;
          return [
            <Select
              id={field.id}
              key={field.id}
              name={field.name}
              description={field.description}
              isRequired={field.isRequired}
              range={field.range}
              options={field.options}
              handleChangeWithId={handleChangeWithId}
              translateKoToEn={translateKoToEn}
              value={
                formFields.find((f) => f.id === id)?.value as SelectOption[]
              }
            />,
          ];
        }
        if (field.type === 'email') {
          const id = field.id;
          const emailValue = formFields.find((f) => f.id === id)
            ?.value as string;

          return [
            <Stack key={id} direction="column" gap={4}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <FormInput
                  id={id}
                  title={field.name}
                  placeholder={field.placeholder}
                  isRequired={field.isRequired}
                  description={field.description}
                  type="email"
                  handleChangeWithId={handleChangeWithId}
                  translateKoToEn={translateKoToEn}
                  value={emailValue}
                  readOnly={isVerified}
                  actionButton={(isInvalid) => {
                    return isEmailVerificationEnabled ? (
                      <Button
                        variant={'skyblue_400_fill'}
                        onClick={() => onSendVerificationCode(emailValue)}
                        disabled={!emailValue || isVerified || isInvalid}
                        style={{
                          width: 100,
                          height: 37,
                        }}
                      >
                        {isVerified
                          ? translateKoToEn('verified')
                          : isCodeSent
                          ? translateKoToEn('resend')
                          : translateKoToEn('send')}
                      </Button>
                    ) : null;
                  }}
                />
              </div>

              {isEmailVerificationEnabled && isCodeSent && !isVerified && (
                <Stack direction="row" gap={8} alignItems="center">
                  <Input
                    type={'number'}
                    placeholder={translateKoToEn('enter-verification-code')}
                    width={'100%'}
                    onChange={(e) => onChangeVerificationCode?.(e.target.value)}
                    value={verificationCode}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                      if (
                        event.key === 'ArrowLeft' ||
                        event.key === 'ArrowRight'
                      ) {
                        event.stopPropagation();
                      }
                    }}
                  />
                  <Button
                    variant={'skyblue_400_fill'}
                    onClick={() => onVerifyCode?.(verificationCode ?? '')}
                    disabled={!verificationCode}
                    style={{
                      width: 72,
                      height: 37,
                    }}
                  >
                    {translateKoToEn('confirm')}
                  </Button>
                </Stack>
              )}
            </Stack>,
          ];
        }

        const id = field.id;
        return [
          <FormInput
            id={id}
            key={id}
            title={field.name}
            placeholder={'placeholder' in field ? field.placeholder : ''}
            isRequired={field.isRequired}
            description={field.description}
            handleChangeWithId={handleChangeWithId}
            translateKoToEn={translateKoToEn}
            value={formFields.find((f) => f.id === id)?.value as string}
          />,
        ];
      })}
    </>
  );
};

export default CtaFormModalContents;
